import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { FormGroup } from '@angular/forms';
import { EngagementService } from 'src/app/care/engagement/engagement.service';
import { KakaotalkTemplate } from 'src/app/parameters/message-templates/kakaotalk-templates/shared';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { Editor2Answer } from 'src/app/shared/models/misc.model';
import { KakaotalkMessage, WhatsappMessage, WhatsappTemplate } from 'src/app/task-process/shared/models';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';

@Injectable({ providedIn: 'root' })
export class ChooseTemplateFlowService {
  clearBS: BehaviorSubject<boolean> = new BehaviorSubject(false);
  templateDisplayedBS: BehaviorSubject<FormGroup> = new BehaviorSubject(null);
  taskProcessContext: TaskProcessContext;
  selectedTemplate: WhatsappTemplate | KakaotalkTemplate;
  hasParameters: boolean;
  isValid: boolean;
  messageData!: {
    content: string;
    attachment?: {
      image: {
        media_id: number;
        media_link: string;
      };
    };
  };

  constructor(private engagementService: EngagementService) {}

  selectedTemplateAsEditor2Answer(): Editor2Answer {
    const messageData: any = this.extractDataFromTemplate(this.selectedTemplate as WhatsappTemplate, true);

    let answer: Editor2Answer = {
      text: this.selectedTemplate.preview,
      links: [],
      attachments: [],
      extra_data: { is_whatsapp_template: true, ...messageData },
    };

    if (this.selectedTemplate.header) {
      const { filename, media_url } = (this.selectedTemplate as WhatsappTemplate).header;
      answer = {
        ...answer,
        headerMediaUrl: media_url,
        headerMediaFilename: filename,
      };
    }
    return answer;
  }

  reset() {
    this.engagementService.flowStep = null;
    this.selectedTemplate = null;
    this.messageData = null;
    this.clearBS.next(true);
  }

  extractDataFromTemplate(selectedTemplate: any, processingPage?: boolean) {
    const { id, header, preview, parameters, buttons, footer, urlButtons, quickReplyButtons, copyCodeButtons } =
      selectedTemplate;

    const params = parameters.map(({ value }) => value);
    let template: any;
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        {
          const urlButtonParams = (urlButtons || []).reduce((result, { text, example }) => {
            result[text] = example[0];
            return result;
          }, {});
          const couponCodeButtonParams = (copyCodeButtons || []).reduce(
            (result, { example }) => {
              result['couponCode'] = example?.length ? example[0] : '';
              return result;
            },
            { couponCode: '' }
          );
          template = {
            [processingPage ? 'id' : 'whatsapp_template_id']: id,
            [processingPage ? 'text' : 'body']: preview,
            params,
            footer,
            buttons,
            button_params: { urlButtonParams, couponCodeButtonParams, quickReplyButtons },
          } as WhatsappMessage;
          if (header) {
            // Snapshot needed to render the media/text in the conversation
            const { media_url, filename, media_type, type } = header;
            template.header = {
              [processingPage ? 'media_url' : 'headerMediaUrl']: media_url,
              [processingPage ? 'filename' : 'headerMediaFilename']: filename,
              media_type,
              type,
            };
          }
        }
        break;
      case SiteTypeEnum.Kakaotalk:
        {
          let urlButtonIndex = 0;
          const buttonsFormatted = (buttons || []).reduce((_buttons, { type, text, url }) => {
            const button = { type, text, url };
            if (/\{\{.+\}\}/.test(url)) {
              button.url = urlButtons[urlButtonIndex].urlFormatted;
              urlButtonIndex++;
            }
            return _buttons.concat(button);
          }, []);

          template = {
            kakaotalk_template_id: id,
            body: preview,
          } as KakaotalkMessage;

          const checkAttachement = () => {
            if (!template.attachment) {
              template.attachment = {};
            }
          };

          if (header?.media_url) {
            checkAttachement();
            const { media_url: media_link, media_id } = header;
            template.attachment.image = {
              media_id,
              media_link,
            };
          }

          if (buttonsFormatted?.length) {
            checkAttachement();
            template.attachment.buttons = buttonsFormatted;
          }
        }
        break;
      default:
        break;
    }
    return template;
  }
}
